import { getCornKernels } from '@eigtech/shared-corn'
import {
  Box,
  Button,
  Center,
  ComposedAlert,
  ComposedCard,
  EmptyState,
  Skeleton,
  Stack,
  StackDivider,
  Text,
} from '@eigtech/ui-shared-dave'
import { UseGroupNotesByDateProps, useGroupNotesByDate } from '@eigtech/ui-shared-notes'
import { range } from 'lodash-es'
import { MdPostAdd } from 'react-icons/md'
import { NoteCard, NoteCardProps } from './NoteCard'

export type NotesListProps = {
  isSearching?: boolean
  isPending?: boolean
  isError?: boolean
  createNote?: () => void
  includePublishField?: boolean
} & UseGroupNotesByDateProps &
  Pick<NoteCardProps, 'actionsSlot' | 'entityCorn'>

export function NotesList({
  actionsSlot,
  entityCorn,
  formatDate,
  isAscending,
  isError,
  isPending,
  isSearching,
  notes,
  createNote,
  includePublishField,
}: NotesListProps) {
  const { resourceType } = getCornKernels(entityCorn)

  const notesGroupedByDate = useGroupNotesByDate({ notes, formatDate, isAscending })

  return (
    <Stack divider={<StackDivider />} spacing="6">
      {isPending ? (
        range(4).map((index) => <Skeleton key={index} borderRadius="md" h="32" />)
      ) : isError ? (
        <ComposedAlert
          alert={{
            title: 'Error',
            description: `There was an issue retrieving this ${resourceType}'s notes.`,
          }}
          status="error"
        />
      ) : !notes.length ? (
        isSearching ? (
          <ComposedAlert alert="No notes found that match your search input." status="info" />
        ) : (
          <ComposedCard>
            <EmptyState heading="No notes have been added" icon={MdPostAdd}>
              <Stack spacing="4">
                <Text>Notes can be added to provide internal updates on this {resourceType}.</Text>
                {!!createNote && (
                  <Center>
                    <Button onClick={createNote}>Add Note</Button>
                  </Center>
                )}
              </Stack>
            </EmptyState>
          </ComposedCard>
        )
      ) : (
        notesGroupedByDate.map(([date, notes]) => (
          <Box key={date}>
            <Text fontWeight="bold" mb="2">
              {date}
            </Text>
            <Stack spacing="4">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  actionsSlot={actionsSlot}
                  entityCorn={entityCorn}
                  includePublishField={includePublishField}
                  note={note}
                />
              ))}
            </Stack>
          </Box>
        ))
      )}
    </Stack>
  )
}
